import { common, InvalidFormatError } from '@neo-one/client-common';
import BN from 'bn.js';
import _ from 'lodash';
export class BinaryReader {
    constructor(buffer, index = 0) {
        this.buffer = buffer;
        this.mutableIndex = index;
    }
    get index() {
        return this.mutableIndex;
    }
    get remaining() {
        return this.buffer.length - this.mutableIndex;
    }
    get remainingBuffer() {
        return this.buffer.slice(this.mutableIndex);
    }
    hasMore() {
        return this.mutableIndex < this.buffer.byteLength;
    }
    clone() {
        return new BinaryReader(this.buffer, this.mutableIndex);
    }
    readBytes(numBytes) {
        this.checkRead(numBytes);
        const result = this.buffer.slice(this.mutableIndex, this.mutableIndex + numBytes);
        this.mutableIndex += numBytes;
        return result;
    }
    readInt8() {
        this.checkRead(1);
        const result = this.buffer.readInt8(this.mutableIndex);
        this.mutableIndex += 1;
        return result;
    }
    readUInt8() {
        this.checkRead(1);
        const result = this.buffer.readUInt8(this.mutableIndex);
        this.mutableIndex += 1;
        return result;
    }
    readBoolean() {
        return this.readBytes(1)[0] !== 0;
    }
    readInt16LE() {
        this.checkRead(2);
        const result = this.buffer.readInt16LE(this.mutableIndex);
        this.mutableIndex += 2;
        return result;
    }
    readUInt16LE() {
        this.checkRead(2);
        const result = this.buffer.readUInt16LE(this.mutableIndex);
        this.mutableIndex += 2;
        return result;
    }
    readUInt16BE() {
        this.checkRead(2);
        const result = this.buffer.readUInt16BE(this.mutableIndex);
        this.mutableIndex += 2;
        return result;
    }
    readInt32LE() {
        this.checkRead(4);
        const result = this.buffer.readInt32LE(this.mutableIndex);
        this.mutableIndex += 4;
        return result;
    }
    readUInt32LE() {
        this.checkRead(4);
        const result = this.buffer.readUInt32LE(this.mutableIndex);
        this.mutableIndex += 4;
        return result;
    }
    readUInt64LE() {
        return new BN(this.readBytes(8), 'le');
    }
    readInt64LE() {
        const buffer = this.readBytes(8);
        return new BN(buffer, 'le').fromTwos(buffer.length * 8);
    }
    readUInt160() {
        return common.bufferToUInt160(this.readBytes(common.UINT160_BUFFER_BYTES));
    }
    readUInt256() {
        return common.bufferToUInt256(this.readBytes(common.UINT256_BUFFER_BYTES));
    }
    readFixed8() {
        return this.readInt64LE();
    }
    readFixedString(length) {
        const values = _.takeWhile([...this.readBytes(length)], (value) => value !== 0);
        return Buffer.from(values).toString('utf8');
    }
    readArray(read, max = 0x1000000) {
        const count = this.readVarUIntLE(new BN(max)).toNumber();
        return _.range(count).map(read);
    }
    readObject(read, max = 0x1000000) {
        const count = this.readVarUIntLE(new BN(max)).toNumber();
        return _.range(count).reduce((acc) => {
            const { key, value } = read();
            return { ...acc, [key]: value };
        }, {});
    }
    readVarBytesLE(max = 0x1000000) {
        return this.readBytes(this.readVarUIntLE(new BN(max)).toNumber());
    }
    readVarUIntLE(max = new BN('18446744073709551615', 10)) {
        const fb = this.readUInt8();
        let value;
        switch (fb) {
            case 0xfd:
                value = new BN(this.readUInt16LE());
                break;
            case 0xfe:
                value = new BN(this.readUInt32LE());
                break;
            case 0xff:
                value = this.readUInt64LE();
                break;
            default:
                value = new BN(fb);
        }
        if (value.gt(max)) {
            throw new InvalidFormatError(`Integer too large: ${value.toString(10)} > ${max.toString(10)}`);
        }
        return value;
    }
    readVarString(max = 0x1000000) {
        return this.readVarBytesLE(max).toString('utf8');
    }
    readECPoint() {
        const firstByte = this.readBytes(1);
        if (firstByte[0] === common.ECPOINT_INFINITY_BYTE) {
            return common.ECPOINT_INFINITY;
        }
        return common.bufferToECPoint(Buffer.concat([firstByte, this.readBytes(common.ECPOINT_BUFFER_BYTES - 1)]));
    }
    checkRead(numBytes) {
        if (this.remaining < numBytes) {
            throw new InvalidFormatError(`Insufficient bytes remaining (${this.remaining}): ${numBytes}`);
        }
    }
}

//# sourceMappingURL=BinaryReader.js.map
